import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  KeyIcon,
  EyeIcon,
  CogIcon,
  ChartBarIcon,
  ClockIcon,
  UserGroupIcon,
  ComputerDesktopIcon,
  CheckCircleIcon,
  XCircleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import axios from 'axios';
import toast from '../utils/notify';
import { DataTable } from './common/DataTable';

const SecurityDashboard = () => {
  const [stats, setStats] = useState(null);
  const [events, setEvents] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [showSettings, setShowSettings] = useState(false);

  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchSecurityData();
  }, []);

  const fetchSecurityData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [statsResponse, eventsResponse, settingsResponse] = await Promise.all([
        axios.get(`${API_BASE}/api/security/dashboard/stats`, { headers }),
        axios.get(`${API_BASE}/api/security/events?limit=20`, { headers }),
        axios.get(`${API_BASE}/api/security/settings`, { headers })
      ]);

      setStats(statsResponse.data.data);
      setEvents(eventsResponse.data.data.events);
      setSettings(settingsResponse.data.data);
    } catch (error) {
      console.error('Error fetching security data:', error);
      toast.error('Failed to load security dashboard');
    } finally {
      setLoading(false);
    }
  };

  const updateSecuritySettings = async (updatedSettings) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE}/api/security/settings`, updatedSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Security settings updated successfully');
      fetchSecurityData();
      setShowSettings(false);
    } catch (error) {
      console.error('Error updating security settings:', error);
      toast.error('Failed to update security settings');
    }
  };

  const testEncryption = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_BASE}/api/security/test/encryption`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.data.test_passed) {
        toast.success('Encryption test passed successfully');
      } else {
        toast.error('Encryption test failed');
      }
    } catch (error) {
      console.error('Error testing encryption:', error);
      toast.error('Encryption test failed');
    }
  };

  const getEventSeverityColor = (severity) => {
    switch (severity) {
      case 'CRITICAL': return 'text-red-600 bg-red-100';
      case 'HIGH': return 'text-orange-600 bg-orange-100';
      case 'WARNING': return 'text-yellow-600 bg-yellow-100';
      case 'INFO': return 'text-blue-600 bg-blue-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const formatEventType = (type) => {
    return type.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center">
                <ShieldCheckIcon className="w-8 h-8 text-purple-600 mr-3" />
                Security Dashboard
              </h1>
              <p className="text-gray-600 mt-1">System-wide security configurations and monitoring</p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={testEncryption}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center"
              >
                <KeyIcon className="w-4 h-4 mr-2" />
                Test Encryption
              </button>
              <button
                onClick={() => setShowSettings(true)}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center"
              >
                <CogIcon className="w-4 h-4 mr-2" />
                Settings
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tab Navigation */}
        <div className="mb-6">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', name: 'Overview', icon: ChartBarIcon },
              { id: 'events', name: 'Security Events', icon: EyeIcon },
              { id: 'monitoring', name: 'Monitoring', icon: ComputerDesktopIcon }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center px-3 py-2 font-medium rounded-lg transition-colors ${
                  activeTab === tab.id
                    ? 'text-purple-700 bg-purple-100'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                }`}
              >
                <tab.icon className="w-5 h-5 mr-2" />
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <div className="space-y-6">
            {/* Security Metrics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <EyeIcon className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Events (24h)</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.overview?.total_events_24h || 0}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Failed Logins</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.overview?.failed_authentications_24h || 0}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <UserGroupIcon className="w-6 h-6 text-yellow-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Data Access</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.overview?.data_access_events_24h || 0}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border p-6">
                <div className="flex items-center">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <CogIcon className="w-6 h-6 text-purple-600" />
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-600">Config Changes</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.overview?.config_changes_7d || 0}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Security Configuration Status */}
            <div className="bg-white rounded-lg shadow-sm border">
              <div className="p-6 border-b">
                <h3 className="text-lg font-semibold text-gray-900">Security Configuration Status</h3>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Data Encryption</span>
                      <span className="flex items-center text-green-600">
                        <CheckCircleIcon className="w-4 h-4 mr-1" />
                        Active
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Rate Limiting</span>
                      <span className="flex items-center text-green-600">
                        <CheckCircleIcon className="w-4 h-4 mr-1" />
                        {settings?.rate_limiting?.requests_per_window || 100} req/min
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Session Timeout</span>
                      <span className="flex items-center text-green-600">
                        <CheckCircleIcon className="w-4 h-4 mr-1" />
                        {Math.floor((settings?.session_security?.timeout_seconds || 3600) / 60)} min
                      </span>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Security Headers</span>
                      <span className="flex items-center text-green-600">
                        <CheckCircleIcon className="w-4 h-4 mr-1" />
                        {settings?.security_headers?.length || 0} active
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Audit Logging</span>
                      <span className="flex items-center text-green-600">
                        <CheckCircleIcon className="w-4 h-4 mr-1" />
                        Active
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Password Policy</span>
                      <span className="flex items-center text-green-600">
                        <CheckCircleIcon className="w-4 h-4 mr-1" />
                        {settings?.password_policy?.min_length || 8} char min
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Top IPs and Users */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-sm border">
                <div className="p-6 border-b">
                  <h3 className="text-lg font-semibold text-gray-900">Top IPs (24h)</h3>
                </div>
                <div className="p-6">
                  <div className="space-y-3">
                    {Object.entries(stats.top_ips || {}).slice(0, 5).map(([ip, count]) => (
                      <div key={ip} className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">{ip}</span>
                        <span className="text-sm text-gray-500">{count} requests</span>
                      </div>
                    ))}
                    {Object.keys(stats.top_ips || {}).length === 0 && (
                      <p className="text-sm text-gray-500">No data available</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border">
                <div className="p-6 border-b">
                  <h3 className="text-lg font-semibold text-gray-900">Top Users (24h)</h3>
                </div>
                <div className="p-6">
                  <div className="space-y-3">
                    {Object.entries(stats.top_users || {}).slice(0, 5).map(([user, count]) => (
                      <div key={user} className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">{user}</span>
                        <span className="text-sm text-gray-500">{count} events</span>
                      </div>
                    ))}
                    {Object.keys(stats.top_users || {}).length === 0 && (
                      <p className="text-sm text-gray-500">No data available</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Security Events Tab */}
        {activeTab === 'events' && (
          <div className="bg-white rounded-lg shadow-sm border">
            <div className="p-6 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Recent Security Events</h3>
            </div>
            <div className="p-2">
              <DataTable
                columns={[
                  {
                    id: 'timestamp',
                    accessorFn: (e) => e.timestamp,
                    header: 'Timestamp',
                    enableSorting: false,
                    cell: ({ row }) => <span>{new Date(row.original.timestamp).toLocaleString()}</span>,
                  },
                  {
                    id: 'event_type',
                    accessorFn: (e) => formatEventType(e.event_type),
                    header: 'Event Type',
                    enableSorting: false,
                    meta: {
                      filter: {
                        variant: 'select',
                        label: 'Event Type',
                        placeholder: 'All event types',
                        options: [...new Set(events.map((e) => formatEventType(e.event_type)).filter(Boolean))].map((v) => ({ value: v, label: v })),
                      },
                    },
                    cell: ({ row }) => <span>{formatEventType(row.original.event_type)}</span>,
                  },
                  {
                    id: 'user_id',
                    accessorFn: (e) => e.user_id,
                    header: 'User',
                    enableSorting: false,
                    meta: { filter: { variant: 'text', label: 'User', placeholder: 'Search user' } },
                    cell: ({ row }) => <span>{row.original.user_id}</span>,
                  },
                  {
                    id: 'severity',
                    accessorFn: (e) => e.severity,
                    header: 'Severity',
                    enableSorting: false,
                    meta: {
                      filter: {
                        variant: 'select',
                        label: 'Severity',
                        placeholder: 'All severities',
                        options: [...new Set(events.map((e) => e.severity).filter(Boolean))].map((v) => ({ value: v, label: v })),
                      },
                    },
                    cell: ({ row }) => (
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getEventSeverityColor(row.original.severity)}`}>
                        {row.original.severity}
                      </span>
                    ),
                  },
                  {
                    id: 'ip_address',
                    accessorFn: (e) => e.client_info?.ip_address || 'N/A',
                    header: 'IP Address',
                    enableSorting: false,
                    meta: { filter: { variant: 'text', label: 'IP Address', placeholder: 'Search IP address' } },
                    cell: ({ row }) => <span>{row.original.client_info?.ip_address || 'N/A'}</span>,
                  },
                ]}
                data={events}
                getRowId={(e, i) => String(e.id ?? i)}
                emptyMessage="No security events found"
                enableColumnVisibility={false}
                enableColumnResizing
                enableColumnFilters
              />
            </div>
          </div>
        )}

        {/* Monitoring Tab */}
        {activeTab === 'monitoring' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Security Monitoring Status</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="p-4 bg-green-100 rounded-full w-16 h-16 mx-auto mb-3 flex items-center justify-center">
                    <CheckCircleIcon className="w-8 h-8 text-green-600" />
                  </div>
                  <h4 className="font-semibold text-gray-900">System Health</h4>
                  <p className="text-sm text-gray-600">All systems operational</p>
                </div>
                <div className="text-center">
                  <div className="p-4 bg-blue-100 rounded-full w-16 h-16 mx-auto mb-3 flex items-center justify-center">
                    <EyeIcon className="w-8 h-8 text-blue-600" />
                  </div>
                  <h4 className="font-semibold text-gray-900">Active Monitoring</h4>
                  <p className="text-sm text-gray-600">Real-time event tracking</p>
                </div>
                <div className="text-center">
                  <div className="p-4 bg-purple-100 rounded-full w-16 h-16 mx-auto mb-3 flex items-center justify-center">
                    <ShieldCheckIcon className="w-8 h-8 text-purple-600" />
                  </div>
                  <h4 className="font-semibold text-gray-900">Security Enabled</h4>
                  <p className="text-sm text-gray-600">All security features active</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Settings Modal */}
      <AnimatePresence>
        {showSettings && settings && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg shadow-lg max-w-2xl w-full mx-4 max-h-screen overflow-y-auto"
            >
              <SecuritySettingsModal
                settings={settings}
                onSave={updateSecuritySettings}
                onClose={() => setShowSettings(false)}
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Security Settings Modal Component
const SecuritySettingsModal = ({ settings, onSave, onClose }) => {
  const [formData, setFormData] = useState({
    rate_limit_requests: settings.rate_limiting?.requests_per_window || 100,
    rate_limit_window: settings.rate_limiting?.window_seconds || 60,
    session_timeout: Math.floor((settings.session_security?.timeout_seconds || 3600) / 60),
    max_concurrent_sessions: settings.session_security?.max_concurrent_sessions || 5,
    password_min_length: settings.password_policy?.min_length || 8,
    password_require_uppercase: settings.password_policy?.require_uppercase || true,
    password_require_lowercase: settings.password_policy?.require_lowercase || true,
    password_require_numbers: settings.password_policy?.require_numbers || true,
    password_require_special: settings.password_policy?.require_special || true,
    password_max_age_days: settings.password_policy?.max_age_days || 90
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const updatedSettings = {
      ...formData,
      session_timeout: formData.session_timeout * 60 // Convert back to seconds
    };
    onSave(updatedSettings);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-900">Security Settings</h2>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600"
        >
          <XCircleIcon className="w-6 h-6" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Rate Limiting */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Rate Limiting</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Requests per Window
              </label>
              <input
                type="number"
                value={formData.rate_limit_requests}
                onChange={(e) => handleChange('rate_limit_requests', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                min="1"
                max="1000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Window (seconds)
              </label>
              <input
                type="number"
                value={formData.rate_limit_window}
                onChange={(e) => handleChange('rate_limit_window', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                min="10"
                max="3600"
              />
            </div>
          </div>
        </div>

        {/* Session Security */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Session Security</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Session Timeout (minutes)
              </label>
              <input
                type="number"
                value={formData.session_timeout}
                onChange={(e) => handleChange('session_timeout', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                min="5"
                max="1440"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Concurrent Sessions
              </label>
              <input
                type="number"
                value={formData.max_concurrent_sessions}
                onChange={(e) => handleChange('max_concurrent_sessions', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                min="1"
                max="50"
              />
            </div>
          </div>
        </div>

        {/* Password Policy */}
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Password Policy</h3>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Minimum Length
                </label>
                <input
                  type="number"
                  value={formData.password_min_length}
                  onChange={(e) => handleChange('password_min_length', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  min="6"
                  max="128"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Max Age (days)
                </label>
                <input
                  type="number"
                  value={formData.password_max_age_days}
                  onChange={(e) => handleChange('password_max_age_days', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                  min="1"
                  max="365"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.password_require_uppercase}
                    onChange={(e) => handleChange('password_require_uppercase', e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Require Uppercase</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.password_require_lowercase}
                    onChange={(e) => handleChange('password_require_lowercase', e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Require Lowercase</span>
                </label>
              </div>
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.password_require_numbers}
                    onChange={(e) => handleChange('password_require_numbers', e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Require Numbers</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.password_require_special}
                    onChange={(e) => handleChange('password_require_special', e.target.checked)}
                    className="mr-2"
                  />
                  <span className="text-sm text-gray-700">Require Special Characters</span>
                </label>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3 pt-4 border-t">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            Save Settings
          </button>
        </div>
      </form>
    </div>
  );
};

export default SecurityDashboard;